import { useMemo } from "react";

import styles from "../../../src/styles/components/leads/LeadDetailsCard.module.css"
import TextField from "../../ui/editable/TextField"
import DropdownField from "../../ui/editable/DropdownField";
import LeadPersonsSection from "./LeadPersonsSection";
import Avatar from "../../ui/avatar/Avatar";
import Toggle from "../../ui/selectors/Toggle";
import {
  buildCountryOptions,
  findCountryOption,
  toCountryOption,
} from "../../utils/countryOptions";

const CUSTOM_FIELD_INPUT_TYPES = {
  text: "text",
  number: "number",
  date: "date",
};

function isCustomFieldValueEnabled(value) {
  return value === true || value === "true" || value === "1";
}

function CustomFieldValue({ field, value, onCommit }) {
  if (field.data_type === "boolean") {
    const isEnabled = isCustomFieldValueEnabled(value);

    return (
      <div className={styles.booleanValue}>
        <Toggle
          checked={isEnabled}
          ariaLabel={field.name}
          onChange={(nextValue) => onCommit?.(nextValue)}
        />
        <span className={styles.textPrimary}>{isEnabled ? "Sí" : "No"}</span>
      </div>
    );
  }

  return (
    <TextField
      value={value}
      placeholder={field.name}
      type={CUSTOM_FIELD_INPUT_TYPES[field.data_type] ?? "text"}
      onCommit={(nextValue) => onCommit?.(nextValue)}
    />
  );
}

function PersonChip({ name, avatar }) {
  if (!name) return null;

  return (
    <div className={styles.personChip}>
      <Avatar src={avatar} name={name} size={24} />
      <span className={styles.personName}>{name}</span>
    </div>
  );
}

function CompanyCountryText({ country, countryOptions }) {
  const countryName = useMemo(() => {
    if (!country) return "";

    const options = buildCountryOptions(countryOptions);
    const countryOption = findCountryOption(options, country) ?? toCountryOption(country);
    return countryOption?.name || "";
  }, [country, countryOptions]);

  return <span className={styles.readOnlyValue}>{countryName || "—"}</span>;
}

function LeadDetailsRow({ label, children, multiline = false, error }) {
  return (
    <div className={`${styles.row} ${multiline ? styles.rowMultiline : ""} ${error ? styles.rowWithError : ""}`}>
      <div className={styles.label}>{label}</div>
      <div className={`${styles.value} ${error ? styles.valueError : ""}`}>
        {children}
      </div>
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}

export default function LeadDetailsCard({
  name,
  responsible,
  account,
  company,
  type,
  country,
  persons = [],
  personPicker = {},
  onAddPerson,
  onRemovePerson,
  onToggleRecipient,
  pendingPersonIds = [],

  customFields = [],
  customFieldValues = {},
  onCustomFieldCommit,

  typeOptions = [],
  countryOptions = [],
  responsibleOptions = [],
  accountOptions = [],
  companyOptions = [],
  companyLoading = false,
  onCompanySearch,
  onCompanyLoadMore,
  onCreateCompany,

  onAddData,
  onFieldChange,
  onFieldCommit,
  addCustomFieldSelector,
  errors = {},
}) {

  return (

    <section className={styles.card}>
      <LeadDetailsRow label="Nombre" error={errors.lead_name}>
        <TextField
          value={name}
          placeholder="Nombre"
          onChange={(value) => onFieldChange?.("lead_name", value)}
          onCommit={(value) => onFieldCommit?.("lead_name", value)}
        />
      </LeadDetailsRow>

      <LeadDetailsRow label="Responsable" error={errors.responsible}>
        <DropdownField
          label="Responsable"
          items={responsibleOptions}
          value={responsible}
          labelKey="name"
          placeholder="Responsable"
          onChange={(newResponsible) => {
            onFieldChange?.("responsible", newResponsible);
            onFieldCommit?.("responsible", newResponsible);
          }}
          renderValue={(person) => (
            <PersonChip
              name={person?.user_name || person?.name || ""}
              avatar={person?.user_photo || person?.photo || person?.avatar || person?.image}
            />
          )}
        />
      </LeadDetailsRow>

      <LeadDetailsRow label="Cuenta" error={errors.account}>
        <DropdownField
          label="Cuenta"
          items={accountOptions || []}
          value={account}
          labelKey="name"
          placeholder="Cuenta"
          onChange={(newAccount) => {
            onFieldChange?.("account", newAccount);
            onFieldCommit?.("account", newAccount);
          }}
          renderValue={(acc) => (
            <span
              className={styles.accountPill}
              style={
                acc?.color
                  ? {
                      backgroundColor: acc.color,
                      color: "#fff",
                    }
                  : {
                      backgroundColor: "var(--color-brand-600, #1A5869)",
                      color: "#fff",
                    }
              }
            >
              <span className={styles.accountPillText}>{acc?.name}</span>
            </span>
          )}
        />
      </LeadDetailsRow>

      <LeadDetailsRow label="Compañía" error={errors.company}>
        <DropdownField
          label="Compañía"
          items={companyOptions || []}
          value={company}
          labelKey="name"
          placeholder="Compañía"
          searchable
          onSearch={onCompanySearch}
          onLoadMore={onCompanyLoadMore}
          loading={companyLoading}
          onChange={(newCompany) => {
            onFieldChange?.("company", newCompany);
            onFieldCommit?.("company", newCompany);
          }}
          footerAction={
            onCreateCompany
              ? { label: "＋ Crear empresa", onClick: onCreateCompany }
              : null
          }
        />
      </LeadDetailsRow>

      <LeadDetailsRow label="Tipo" error={errors.type}>
        <DropdownField
          label="Tipo"
          items={typeOptions || []}
          value={type}
          labelKey="name"
          placeholder="Tipo"
          onChange={(newType) => {
            onFieldChange?.("type", newType);
            onFieldCommit?.("type", newType);
          }}
        />
      </LeadDetailsRow>

      <LeadDetailsRow label="País">
        <CompanyCountryText country={country} countryOptions={countryOptions} />
      </LeadDetailsRow>

      <LeadDetailsRow label="Personas" multiline error={errors.person_ref}>
        <LeadPersonsSection
          persons={persons}
          personPicker={personPicker}
          onAddPerson={onAddPerson}
          onRemovePerson={onRemovePerson}
          onToggleRecipient={onToggleRecipient}
          pendingPersonIds={pendingPersonIds}
        />
      </LeadDetailsRow>

      {customFields.map((field) => (
        <LeadDetailsRow key={field.id} label={field.name}>
          <CustomFieldValue
            field={field}
            value={customFieldValues?.[field.id]?.value ?? ""}
            onCommit={(value) => onCustomFieldCommit?.(field, value)}
          />
        </LeadDetailsRow>
      ))}

      <div className={styles.addCustomFieldRow}>
        {addCustomFieldSelector ? (
          <div className={styles.addCustomFieldForm}>{addCustomFieldSelector}</div>
        ) : (
          <button type="button" className={styles.addButton} onClick={onAddData}>
            <span className={styles.addIcon}>＋</span>
            <span>Agregar dato</span>
          </button>
        )}
      </div>
    </section>
  );
}
